import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
  importProvidersFrom,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  LucideAngularModule,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronsLeftRight,
  LayoutGrid,
  Users,
  CreditCard,
  Settings,
  Trash2,
  RotateCcw,
  RotateCw,
  CircleQuestionMark,
  Circle,
  Search,
  Plus,
  Bell,
  List,
  EllipsisVertical,
  Menu,
  X,
  Home,
  FolderOpen,
  ClipboardList,
  Bookmark,
  Filter,
  ListFilter,
  ExternalLink,
  Pencil,
  AlertTriangle,
  Loader2,
  Layers,
  Send,
  CheckCircle,
  CircleX,
  File,
  FileText,
  Calendar,
  Flag,
  CirclePlay,
  CirclePause,
  User,
  Lock,
  Building,
  Copy,
  Check,
  Plug,
  Hash,
  Repeat,
  RefreshCw,
  AlertCircle,
  Info,
  Upload,
  Image,
  Globe,
  Rocket,
  Code,
  MousePointerClick,
  Palette,
  Bug,
  Link,
  Bold,
  Italic,
  Underline,
  Heading1,
  Heading2,
  ListOrdered,
  ListChecks,
  PanelRightClose,
  Ellipsis,
  MessageSquare,
  History,
  LayoutList,
  Download,
  Eye,
  EyeOff,
  Building2,
  ArrowLeft,
  Mail,
  MailPlus,
  StickyNote,
  Columns3Cog,
  GripVertical,
  CircleCheck,
  Inbox,
  CornerDownRight,
  MessageCircle,
  FlaskConical,
  CheckCheck,
  BellOff,
  UserPlus,
  AtSign,
  ArrowRight,
  LoaderCircle,
  FilePlus,
  ToggleLeft,
  LifeBuoy,
  PanelLeftClose,
  PanelLeftOpen,
  Folder,
  Ticket,
  UsersRound,
  LayoutDashboard,
  ChevronUp,
  UserX,
  SearchX,
  UserCheck,
  UserCog,
  UserRoundPen,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Workflow,
  ListTree,
  CheckCircle2,
  CalendarClock,
  CornerDownLeft,
  ThumbsUp,
  ThumbsDown,
  Zap,
  CalendarCheck,
  Activity,
  Clock,
  LogOut,
  Hand,
  Dot,
  Pin,
  FolderPlus,
  Tag,
  Maximize2,
  Minimize2,
  SquareCheck,
  CircleCheckIcon,
  MessagesSquare,
  Reply,
  ArrowDownUp,
  Camera,
  Gem,
  MessageSquareMore,
  Network,
  Package,
  SlidersHorizontal,
  SwatchBook,
  CloudUpload,
  Sparkles,
  ShieldCheck,
  Coins,
  TriangleAlert,
  Box,
  Database,
  FilePen,
  GitBranch,
  Mic,
  PanelTop,
  Paperclip,
  CircleStop,
  Undo2,
} from 'lucide-angular';

import { routes } from './app.routes';
import { authInterceptor } from './shared/interceptors/auth.interceptor';
import { SessionService } from './shared/services/session.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor])),
    /*
     * Identity is rebuilt from the server on every app start, and this is where that starts.
     *
     * It deliberately returns nothing rather than the promise: an initializer that returns a
     * promise blocks bootstrap until it settles, which would trade the stale-role bug for a
     * blank screen for as long as the network takes. The shell paints immediately with
     * skeletons; whoever actually cannot decide without an answer — the two organization
     * guards — awaits `load()`, which hands back this same in-flight request.
     */
    provideAppInitializer(() => {
      inject(SessionService).load();
    }),
    importProvidersFrom(
      LucideAngularModule.pick({
        ChevronLeft,
        ChevronRight,
        LayoutGrid,
        Users,
        PanelRightClose,
        Ellipsis,
        Circle,
        CreditCard,
        Settings,
        Trash2,
        RotateCcw,
        RotateCw,
        CircleQuestionMark,
        Search,
        Plus,
        Bell,
        FolderPlus,
        Pin,
        MessageSquare,
        LayoutList,
        History,
        List,
        SearchX,
        EllipsisVertical,
        Menu,
        Building2,
        ArrowLeft,
        Mail,
        MailPlus,
        Columns3Cog,
        X,
        CircleCheck,
        CornerDownRight,
        MessageCircle,
        Home,
        FolderOpen,
        ClipboardList,
        GripVertical,
        Bookmark,
        Filter,
        ListFilter,
        ChevronDown,
        ExternalLink,
        GitBranch,
        TriangleAlert,
        Pencil,
        Inbox,
        AlertTriangle,
        Loader2,
        Layers,
        Send,
        Download,
        CheckCircle,
        CircleX,
        File,
        FileText,
        Calendar,
        Flag,
        CirclePlay,
        CirclePause,
        User,
        Lock,
        Building,
        Copy,
        Check,
        Plug,
        Hash,
        Repeat,
        RefreshCw,
        AlertCircle,
        Info,
        Upload,
        Image,
        Globe,
        Rocket,
        Code,
        MousePointerClick,
        Palette,
        Bug,
        Link,
        Bold,
        Italic,
        Underline,
        Heading1,
        Heading2,
        ListOrdered,
        ListChecks,
        Eye,
        EyeOff,
        StickyNote,
        FlaskConical,
        CheckCheck,
        BellOff,
        UserPlus,
        AtSign,
        ArrowRight,
        LoaderCircle,
        FilePlus,
        ToggleLeft,
        LifeBuoy,
        PanelLeftClose,
        PanelLeftOpen,
        Folder,
        Ticket,
        UsersRound,
        LayoutDashboard,
        ChevronUp,
        UserX,
        UserCheck,
        UserCog,
        UserRoundPen,
        ArrowUp,
        ArrowDown,
        ArrowUpDown,
        Workflow,
        ListTree,
        CheckCircle2,
        CalendarClock,
        CornerDownLeft,
        ThumbsUp,
        ThumbsDown,
        Zap,
        CalendarCheck,
        Activity,
        Clock,
        LogOut,
        Hand,
        Dot,
        Tag,
        Maximize2,
        Minimize2,
        SquareCheck,
        CircleCheckIcon,
        MessagesSquare,
        Reply,
        ArrowDownUp,
        Camera,
        Gem,
        MessageSquareMore,
        Network,
        Package,
        SlidersHorizontal,
        SwatchBook,
        ChevronsLeftRight,
        CloudUpload,
        Sparkles,
        ShieldCheck,
        Coins,
        Box,
        Database,
        FilePen,
        Mic,
        PanelTop,
        Paperclip,
        CircleStop,
        Undo2,
      }),
    ),
  ],
};
